import * as React from "react";
import { cn } from "../../lib/cn";

/**
 * ActionIconBadge — the orange-tinted square that hosts the action-type
 * icon across every HITL surface (notification chip, focused view header,
 * inline drawer, AISummaryPanel cards). One badge, one rendering, one
 * source of polish.
 *
 * The visual contract was previously duplicated 3x (NotificationChip,
 * FocusedViewLayout chrome, ActionDrawerHeader), with subtly different
 * sizes / corner radii / icon strokes per call site. This atom normalises
 * to the May 2026 design-system tokens (`--action-icon-bg` /
 * `--action-icon-fg` / consistent 1.75 stroke).
 */
export type ActionIconBadgeSize = "sm" | "md" | "lg";

const SIZE_CLASS: Record<ActionIconBadgeSize, string> = {
  sm: "h-6 w-6 rounded-md",
  md: "h-9 w-9 rounded-[10px]",
  lg: "h-12 w-12 rounded-xl",
};

const ICON_SIZE: Record<ActionIconBadgeSize, number> = {
  sm: 12,
  md: 16,
  lg: 20,
};

export interface ActionIconBadgeProps {
  /** Lucide-style icon (or any SVG). Rendered at the size determined by `size`. */
  readonly icon: React.ReactNode;
  /** `sm` (24px chip), `md` (36px header), `lg` (48px focused chrome). */
  readonly size?: ActionIconBadgeSize;
  /**
   * Tone — `accent` (default, orange) for AI proposals; `neutral` for
   * non-actionable previews; `success` after execution.
   */
  readonly tone?: "accent" | "neutral" | "success";
  readonly className?: string;
}

const TONE_CLASS: Record<NonNullable<ActionIconBadgeProps["tone"]>, string> = {
  accent: "bg-ando-orange-soft text-ando-orange",
  neutral: "bg-ink-1/[0.06] text-ink-2",
  success: "bg-success/15 text-success",
};

/**
 * The cloning helper sizes the icon predictably regardless of whether the
 * caller passed a Lucide component or a raw `<svg>`. We coerce the
 * `className` + `size` props on the cloned element; if the icon isn't a
 * valid React element we render it untouched (defensive).
 */
function sizedIcon(
  icon: React.ReactNode,
  size: ActionIconBadgeSize,
): React.ReactNode {
  if (!React.isValidElement(icon)) return icon;
  const numeric = ICON_SIZE[size];
  return React.cloneElement(icon as React.ReactElement<{
    size?: number;
    strokeWidth?: number;
    className?: string;
    "aria-hidden"?: boolean | "true" | "false";
  }>, {
    size: numeric,
    strokeWidth: 1.85,
    "aria-hidden": true,
  });
}

export const ActionIconBadge = React.forwardRef<
  HTMLSpanElement,
  ActionIconBadgeProps & React.HTMLAttributes<HTMLSpanElement>
>(function ActionIconBadge(
  { icon, size = "md", tone = "accent", className, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        SIZE_CLASS[size],
        TONE_CLASS[tone],
        className,
      )}
      {...rest}
    >
      {sizedIcon(icon, size)}
    </span>
  );
});
ActionIconBadge.displayName = "ActionIconBadge";
